import React, { useEffect } from 'react';
import { Constants } from '@praktio/common';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

// Ducks
import { RootState } from '../../ducks/configureStore';
import { updatePlayer } from '../../ducks/explainerReducer';

import InstructionalPanel from '../InstructionalPanel/InstructionalPanel';
import InteractivePanel from '../InteractivePanel/InteractivePanel';
import Explainer from '../SlideTypes/Explainer/Explainer';
import AdaptivePerformanceReport from '../SlideTypes/AdaptivePerformanceReport/AdaptivePerformanceReport';
import PrePostPeformanceReport from '../SlideTypes/PrePostPerformanceReport/PrePostPerformanceReport';

import './Slide.scss';
import { WindowSizes } from '../../utils/constants';

const { CataTypes, ReportTypes, TemplateTypes, ResourceTypes } = Constants;

interface IProps {
  disableAutoPlay?: boolean;
  windowSize: WindowSizes;
}

const Slide = ({ disableAutoPlay, windowSize }: IProps) => {
  const dispatch = useDispatch();
  // LOCAL STATE
  // TODO - Get layout orientation from LMS

  // REDUX STATE
  const {
    currentSlide,
    hasLandscapeDoc,
    hasPortraitDoc,
    layoutIsHorizontal,
    panelIds,
    referenceContent,
    reportType,
    slideType,
  } = useSelector((state: RootState) => {
    const { slidesLitmos } = state;
    const { currentSlide } = slidesLitmos;
    const {
      panels,
      timeline,
      referenceContent,
      subType: slideSubType,
      type: slideType,
      report: reportType,
    } = currentSlide || {};
    const panelIds = panels?.map((panel) => {
      return panel._id;
    });

    const layoutIsHorizontal = slideSubType === CataTypes.EditHorizontal;
    const arrayOfLandscapeDocs = timeline?.filter(
      (timelineElement) =>
        timelineElement.isLandscape || timelineElement.type === ResourceTypes.Image,
    );

    const idOfMainPanel = panels?.[panels.length - 1]._id;
    const hasPortraitDoc = !!timeline?.find(
      (timelineElement) =>
        timelineElement.panelId === idOfMainPanel &&
        (timelineElement.type === ResourceTypes.Document ||
          timelineElement.type === ResourceTypes.Text) &&
        !timelineElement.isLandscape,
    );
    const hasLandscapeDoc = !!(arrayOfLandscapeDocs && arrayOfLandscapeDocs?.length > 0);

    return {
      currentSlide,
      hasLandscapeDoc,
      hasPortraitDoc,
      layoutIsHorizontal,
      panelIds,
      referenceContent,
      reportType,
      slideType,
    };
  });

  useEffect(() => {
    if (!disableAutoPlay) return;
    dispatch(updatePlayer({ playing: false }));
  }, [disableAutoPlay, dispatch]);

  if (currentSlide && slideType === TemplateTypes.Explanation) {
    return (
      <div
        className={classNames('Slide', windowSize, {
          'has-landscape-doc': hasLandscapeDoc,
          'has-portrait-doc': hasPortraitDoc,
        })}
      >
        <Explainer
          disableAutoPlay={disableAutoPlay}
          windowSize={windowSize}
          hasLandscapeDoc={hasLandscapeDoc}
          hasPortraitDoc={hasPortraitDoc}
        />
      </div>
    );
  }

  if (currentSlide && slideType === TemplateTypes.Conclusions) {
    if (reportType === ReportTypes.Performance) {
      return (
        <div
          className={classNames('Slide', windowSize, {
            'has-supplemental-doc': referenceContent,
          })}
        >
          <AdaptivePerformanceReport />
        </div>
      );
    }
    if (reportType === ReportTypes.PrePost) {
      return (
        <div className={classNames('Slide', windowSize)}>
          <PrePostPeformanceReport />
        </div>
      );
    }
    // TODO - For testing purposes. LMS will filter out this style of conclusion
    // slide in the future.
    if (reportType === 'NONE') {
      return (
        <div className="Slide">
          <div>Complete! You finished the module.</div>
        </div>
      );
    }
  }

  return (
    <div
      className={classNames('Slide', windowSize, {
        'has-landscape-doc': hasLandscapeDoc,
        'has-portrait-doc': hasPortraitDoc,
        'has-supplemental-doc': referenceContent,
      })}
    >
      <InstructionalPanel
        panelIds={panelIds}
        layoutIsHorizontal={layoutIsHorizontal}
        windowSize={windowSize}
      />
      <InteractivePanel
        panelIds={panelIds}
        layoutIsHorizontal={layoutIsHorizontal}
        hasLandscapeDoc={hasLandscapeDoc}
        hasPortraitDoc={hasPortraitDoc}
        windowSize={windowSize}
        supplementalDoc={referenceContent}
      />
    </div>
  );
};

export default Slide;
